import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { EnvironmentalConditionDto } from 'models/pen/EnvironmentalCondition';
import { GlobalAnalysisDto } from 'models/pen/GlobalAnalysis';
import { ErrorService } from 'services/error-service';
import { EnvironmentalConditionService } from 'services/pen/environmental-condition-service';
import { GlobalAnalysisService } from 'services/pen/global-analysis-service';
import { ToastService } from 'services/toast-service';

@autoinject
export class EnvironmentalConditionContainer {
  protected headers: {
    title: string;
  }[] = [];
  protected environmentalConditions: EnvironmentalConditionDto[] = [];
  protected globalAnalyses: GlobalAnalysisDto[] = [];

  constructor(
    private errorService: ErrorService,
    private toast: ToastService,
    private environmentalConditionService: EnvironmentalConditionService,
    private globalAnalysisService: GlobalAnalysisService,
    private confirmDialog: ConfirmDialog,
    private t: I18N
  ) {}

  protected async attached() {
    this.createHeaders();
    await Promise.all([this.getEnvironmentalConditions(), this.getGlobalAnalyses()]);
  }

  @AsyncErrorHandler
  private async getEnvironmentalConditions() {
    this.environmentalConditions = await this.environmentalConditionService.getAll();
  }

  @AsyncErrorHandler
  private async getGlobalAnalyses() {
    this.globalAnalyses = await this.globalAnalysisService.getAll();
  }

  protected getGlobalAnalysisName(id?: number): string {
    if (!id) return '';
    const ga = this.globalAnalyses.find((g) => g.Id === id);
    return ga ? ga.AnalysisNo ?? ga.Name ?? '' : '';
  }

  private async onDelete(id: number) {
    const confirmed = await this.confirmDialog.confirmDelete();
    if (!confirmed) return;
    await this.environmentalConditionService.delete(id);
    this.toast.showSuccess(this.t.tr('general.deleted'));
    await this.getEnvironmentalConditions();
  }

  @AsyncErrorHandler
  private async onSave(environmentalConditionDto: EnvironmentalConditionDto) {
    if (environmentalConditionDto.Id) {
      await this.environmentalConditionService.put(environmentalConditionDto, environmentalConditionDto.Id);
    } else {
      await this.environmentalConditionService.post(environmentalConditionDto);
    }
    this.toast.showSuccess(this.t.tr('general.saved'));
    environmentalConditionDto.Id ? this.editingEnvironmentalCondition = undefined : this.isCreating = false;
    await this.getEnvironmentalConditions();
  }

  protected isCreating = false;
  protected editingEnvironmentalCondition: number | undefined = undefined;
  protected setEditingEnvironmentalCondition(id: number) {
    this.isCreating = false;
    this.editingEnvironmentalCondition = id;
  }

  protected setIsCreatingEnvironmentalCondition(isCreating: boolean) {
    this.editingEnvironmentalCondition = undefined;
    this.isCreating = isCreating;
  }

  private createHeaders() {
    this.headers = [
      { title: this.t.tr('general.globalAnalysis') },
      { title: this.t.tr('site.hs') },
      { title: this.t.tr('site.tp') },
      { title: this.t.tr('site.vc') },
      { title: this.t.tr('general.sortIndex') },
      { title: this.t.tr('general.actions') },
    ];
  }
}
